import { baseClient } from './baseRequest';
import { PopulatedKlydo } from './klydo';
import { PendulumLogoType } from '../consts/pendulumLogo';

export enum CollectionReviewStates {
  Approved = 'approved',
  Pending = 'pending',
  Rejected = 'rejected',
  Draft = 'draft',
}

export enum MarketplaceStatus {
  True = 'true',
  False = 'false',
  Pending = 'pending',
}

export type DateValue = { year: number; month: number; day: number };
export type MarketplaceInfo = { status: MarketplaceStatus; date?: DateValue };

export enum CollectionType {
  Draft = 'DraftCollection',
  Approved = 'ApprovedCollection',
}

export type collectionFilters = {
  approved?: boolean;
  pending?: boolean;
  rejected?: boolean;
  draft?: boolean;
  marketplace?: boolean;
};

export type TextSearchOptions = {
  limit?: number;
  skip?: number;
  filters?: collectionFilters;
};

export type SearchInput = {
  options?: TextSearchOptions;
  searchFields?: string[];
};

export type CollectionReviewStatus = {
  state: CollectionReviewStates;
  message?: string;
  /** The artist's reply to `message`, sent with a resubmission. */
  comment?: string;
};

export type ProductPageMetadata = {
  logoTypeUrl?: string;
  thumbnailBackgroundUrl?: string;
  badgeColorHex?: string;
  labels?: string[];
  shopifyProductId?: string;
  affiliateBundleShopifyProductId?: string;
  bundleProductCartImage?: string;
  mockTemplateId?: string;
  thumbnailKlydos?: string[];
  credits?: string;
};

export type PaymentDetails = {
  isAdvancedActive?: boolean;
  planId?: string;
};

export type Collection = {
  type: CollectionType;
  id: string;
  name: string;
  coverUrl: string;
  logoUrl: string;
  klydoIds: string[];
  description: string;
  createdAt: Date;
  updatedAt: Date;
  creatorId: string;
  holdPendulumLogo: boolean;
  marketplace?: MarketplaceInfo;
  reviewStatus: CollectionReviewStatus;
  productPageMetadata?: ProductPageMetadata;
  paymentDetails?: PaymentDetails;
  // Given away through a multi-use code instead of sold (TK-634), so it has
  // redemptions instead of purchases and revenue (TK-772).
  sponsored?: boolean;
  // Whether logoUrl gets padded to pendulumLogoSize ('sized', the default)
  // or baked to cover-crop the whole bob ('full'). Setting 'full' is
  // rejected server-side unless the collection owner is a B2B user (TK-688),
  // or the save comes from an admin impersonating the artist (TK-761).
  // NOT draft-only, unlike pendulumLogoSize: approval never erases it, and
  // it's still needed post-approval to know whether the now-baked logoUrl
  // should render tinted ('sized') or in its own true colors ('full').
  pendulumLogoType?: PendulumLogoType;
};

export type DraftCollection = Collection & {
  type: CollectionType.Draft;
  // Diameter, in device px, the pendulum logo renders at on the clock's bob
  // (410px across). Unset means "not chosen yet" - the raw upload is served as-is.
  // null explicitly resets to the API's legacy default (see the Studio size
  // picker's Reset control); undefined/omitted means "this save doesn't
  // touch it". Draft-only: once a collection is approved, the api bakes this
  // size into logoUrl itself and stops returning the field at all.
  pendulumLogoSize?: number | null;
};

export type PopulatedDraftCollection = DraftCollection & {
  klydos: PopulatedKlydo[];
};

export type ApprovedCollection = Collection & {
  type: CollectionType.Approved;
};

export type PopulatedApprovedCollection = ApprovedCollection & {
  klydos: PopulatedKlydo[];
};

export type CollectionUnion =
  | PopulatedDraftCollection
  | PopulatedApprovedCollection;

export enum OrderGraphPeriod {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}

export type CollectionAnalyticsQueryParams = {
  collectionId: string;
  period?: OrderGraphPeriod;
  enabled?: boolean;
};

export type CollectionStats = {
  totalOrders: number;
  totalSales: number;
  // Sent only for a sponsored collection. All-time, whatever the period.
  totalRedemptions?: number;
};

export type CountryPerformance = {
  country: string;
  percentage: number;
  country_total_order: number;
};

export type OrderGraphData = {
  totalOrders: number;
  graphData: Array<{
    label: string;
    count: number;
  }>;
};

export type CollectionApiClient = {
  upsertCollection: (
    packId: string,
    pack: Partial<DraftCollection>,
  ) => Promise<void>;
  checkNameAvailable: (
    collectionId: string,
    name: string,
  ) => Promise<{ available: boolean }>;
  deleteCollection: (packId: string) => Promise<void>;
  getCollection: (packId: string) => Promise<CollectionUnion>;
  getCollections: ({
    options,
    searchFields,
  }: SearchInput) => Promise<CollectionUnion[]>;
  getAllCollectionsStats: (
    period?: OrderGraphPeriod,
  ) => Promise<CollectionStats>;
  getAllCollectionsCountryPerformance: (
    period?: OrderGraphPeriod,
  ) => Promise<CountryPerformance[]>;
  getAllCollectionsOrdersGraph: (
    period?: OrderGraphPeriod,
  ) => Promise<OrderGraphData>;
  getCollectionStats: (
    collectionId: string,
    period?: OrderGraphPeriod,
  ) => Promise<CollectionStats>;
  getCollectionCountryPerformance: (
    collectionId: string,
    period?: OrderGraphPeriod,
  ) => Promise<CountryPerformance[]>;
  getCollectionOrdersGraph: (
    collectionId: string,
    period?: OrderGraphPeriod,
  ) => Promise<OrderGraphData>;
  getAffiliateLink: (collectionId: string) => Promise<{ url: string }>;
  sendCollectionToClock: (collectionId: string) => Promise<void>;
};

const collectionApiClient: CollectionApiClient = {
  upsertCollection: async (collectionId, collection) => {
    return baseClient.put<void>({
      url: `/studio/collections/${collectionId}`,
      body: collection,
    });
  },
  checkNameAvailable: async (collectionId, name) => {
    return baseClient.get<{ available: boolean }>({
      url: `/studio/collections/${collectionId}/name-available`,
      params: { name },
    });
  },
  deleteCollection: async (collectionId) => {
    return baseClient.delete<void>({
      url: `/studio/collections/${collectionId}`,
    });
  },
  sendCollectionToClock: async (collectionId) => {
    return baseClient.post<void>({
      url: `/studio/collections/${collectionId}/send-to-clock`,
    });
  },
  getCollection: async (collectionId) => {
    return baseClient.get<CollectionUnion>({
      url: `/studio/collections/${collectionId}`,
    });
  },
  getCollections: async ({ options, searchFields }) => {
    return baseClient.post<CollectionUnion[]>({
      url: `/studio/collections/.search/text`,
      body: {
        options,
        searchFields,
      },
    });
  },
  getAllCollectionsStats: async (period = OrderGraphPeriod.WEEK) => {
    return baseClient.get<CollectionStats>({
      url: `/studio/collections/analytics/stats?period=${period}`,
    });
  },
  getAllCollectionsCountryPerformance: async (
    period = OrderGraphPeriod.WEEK,
  ) => {
    return baseClient.get<CountryPerformance[]>({
      url: `/studio/collections/analytics/country-performance?period=${period}`,
    });
  },
  getAllCollectionsOrdersGraph: async (period = OrderGraphPeriod.WEEK) => {
    return baseClient.get<OrderGraphData>({
      url: `/studio/collections/analytics/orders-graph?period=${period}`,
    });
  },
  getCollectionStats: async (collectionId, period) => {
    return baseClient.get<CollectionStats>({
      url: `/studio/collections/${collectionId}/analytics/stats${period ? `?period=${period}` : ''}`,
    });
  },
  getCollectionCountryPerformance: async (
    collectionId,
    period = OrderGraphPeriod.WEEK,
  ) => {
    return baseClient.get<CountryPerformance[]>({
      url: `/studio/collections/${collectionId}/analytics/country-performance?period=${period}`,
    });
  },
  getCollectionOrdersGraph: async (
    collectionId,
    period = OrderGraphPeriod.WEEK,
  ) => {
    return baseClient.get<OrderGraphData>({
      url: `/studio/collections/${collectionId}/analytics/orders-graph?period=${period}`,
    });
  },
  getAffiliateLink: async (collectionId: string): Promise<{ url: string }> => {
    return baseClient.get<{ url: string }>({
      url: `/affiliate-bundle/collections/${collectionId}/link`,
    });
  },
};

export default collectionApiClient;
